import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createOrUpdateIntegrationSetting,
  getIntegrationSettingByUser,
  deleteIntegrationSettingByUuid,
  getIntegrationSettingByUuid,
  refreshIntegrationSettingAPI,
  getConfigurations,
  getIntegrationGroups,
  getConversationsByIntegrationSettingId,
  sendMessage,
  sendReply,
} from "./integrationSettingAPI";

const getSettingId = (g) => g.integrationSettingId ?? g.key;

const parseGroups = (payload) =>
  Array.isArray(payload) ? payload : payload?.groups || [];

const parseConversation = (payload) =>
  Array.isArray(payload)
    ? payload[0] || null
    : (payload?.conversation ?? null);

export const upsertIntegrationSetting = createAsyncThunk(
  "integrationSetting/upsert",
  async (data, thunkAPI) => {
    try {
      const res = await createOrUpdateIntegrationSetting(data);
      return res?.data ?? res;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data ?? { message: error.message },
      );
    }
  },
);

export const fetchIntegrationSettingsByUserId = createAsyncThunk(
  "integrationSetting/fetchAllIntegrationSettingsForUser",
  async (search = "", thunkAPI) => {
    try {
      const res = await getIntegrationSettingByUser(search);
      return res?.data ?? res;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data ?? { message: error.message },
      );
    }
  },
);

export const deleteIntegrationSetting = createAsyncThunk(
  "integrationSetting/delete",
  async ({ uuid, deleteMode }, thunkAPI) => {
    try {
      const res = await deleteIntegrationSettingByUuid({
        uuid,
        deleteMode,
      });

      return res;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data ?? {
          message: error.message,
        },
      );
    }
  },
);

export const fetchIntegrationSettingById = createAsyncThunk(
  "integrationSetting/fetchIntegrationSettingById",
  async ({ settingUuid }, thunkAPI) => {
    try {
      const res = await getIntegrationSettingByUuid(settingUuid);
      return res?.data ?? res;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data ?? { message: error.message },
      );
    }
  },
);

export const refreshIntegrationSetting = createAsyncThunk(
  "integrationSetting/refreshIntegrationSetting",
  async (arg = {}, thunkAPI) => {
    try {
      const res = await refreshIntegrationSettingAPI(arg?.settingId ?? null);
      const data = res?.data ?? res;

      const failed = data?.results?.find((r) => r.status === "failed");
      if (failed) {
        return thunkAPI.rejectWithValue({
          message: failed.error || "Sync failed. Check your API key.",
        });
      }

      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data ?? { message: error.message },
      );
    }
  },
);

export const fetchIntegrationConfiguration = createAsyncThunk(
  "integrationSetting/fetchIntegrationConfiguration",
  async ({ settingUuid, teamId }, thunkAPI) => {
    try {
      const res = await getConfigurations(settingUuid, teamId);
      return res?.data ?? res;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data ?? { message: error.message },
      );
    }
  },
);

export const fetchIntegrationConversation = createAsyncThunk(
  "integrationSetting/fetchIntegrationConversation",
  async (_, thunkAPI) => {
    try {
      const res = await getIntegrationGroups();
      const data = res?.data ?? res;
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data ?? { message: error.message },
      );
    }
  },
);

export const fetchConversationBySettingId = createAsyncThunk(
  "integrationSetting/fetchConversationBySettingId",
  async (integrationSettingId, thunkAPI) => {
    try {
      const res =
        await getConversationsByIntegrationSettingId(integrationSettingId);
      const data = res?.data ?? res;
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data ?? { message: error.message },
      );
    }
  },
);

export const syncAllConversations = createAsyncThunk(
  "integrationSetting/syncAllConversations",
  async (_, thunkAPI) => {
    try {
      const res = await getIntegrationGroups();
      const groupsPayload = res?.data ?? res;
      const groups = parseGroups(groupsPayload);

      const ids = [
        ...new Set(
          groups
            .filter((g) => g.key !== "all")
            .map(getSettingId)
            .filter(Boolean),
        ),
      ];

      const results = await Promise.allSettled(
        ids.map((id) => getConversationsByIntegrationSettingId(id)),
      );

      const conversations = {};
      results.forEach((r, i) => {
        if (r.status === "fulfilled") {
          const data = r.value?.data ?? r.value;
          conversations[ids[i]] = parseConversation(data);
        }
      });

      return { groups, conversations };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data ?? { message: error.message },
      );
    }
  },
  {
    condition: (_, { getState }) => !getState().integrationSetting.syncing,
  },
);

export const sendIntegrationMessage = createAsyncThunk(
  "integrationSetting/sendMessage",
  async (
    { integrationSettingUuid, channelId, recipientId, message },
    thunkAPI,
  ) => {
    try {
      const res = await sendMessage({
        integrationSettingUuid,
        channelId,
        recipientId,
        message,
      });
      return res?.data ?? res;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data ?? { message: error.message },
      );
    }
  },
);

export const sendMessageReply = createAsyncThunk(
  "integrationSetting/sendReply",
  async (
    { integrationSettingUuid, messageId, channelId, recipientId, message },
    thunkAPI,
  ) => {
    try {
      const res = await sendReply({
        integrationSettingUuid,
        messageId,
        channelId,
        recipientId,
        message,
      });
      return res?.data ?? res;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data ?? { message: error.message },
      );
    }
  },
);

const initialState = {
  setting: null,
  settings: [],
  configurations: null,
  conversations: {}, 
  conversationGroups: [],
  conversationsLoading: false,
  currentConversation: null,
  currentConversationId: null, 
  currentConversationLoading: false,
  integrationSettings: [],
  loading: false,
  error: null,
  configLoading: false,
  sendingMessage: false,
  syncing: false,
  sync: [],
};

const extractSyncedMessage = (payload) => {
  if (!payload) return null;
  if (payload.data && typeof payload.data === "object") {
    return payload.data;
  }
  return payload;
};

const applyUpdatedSyncedMessage = (state, updatedMessage) => {
  if (!updatedMessage) return;
  const targetId = updatedMessage.externalMessageId || updatedMessage.uuid;
  if (!targetId) return;

  const updateMessageList = (messages) => {
    if (!Array.isArray(messages)) return;
    const index = messages.findIndex(
      (m) => (m.externalMessageId || m.uuid) === targetId,
    );
    if (index !== -1) {
      messages[index] = {
        ...messages[index],
        ...updatedMessage,
        threads:
          updatedMessage.threads || messages[index].threads || [],
      };
    }
  };

  if (state.currentConversation?.messages) {
    updateMessageList(state.currentConversation.messages);
  }

  Object.values(state.conversations || {}).forEach((conv) => {
    if (conv?.messages) {
      updateMessageList(conv.messages);
    }
  });
};

const applyNewSyncedMessage = (state, newMessage) => {
  if (!newMessage) return;

  const addMessageToList = (conv) => {
    if (!conv || !Array.isArray(conv.messages)) return;
    const targetId = newMessage.externalMessageId || newMessage.uuid;
    const exists = conv.messages.some(
      (m) => (m.externalMessageId || m.uuid) === targetId,
    );
    if (!exists) {
      conv.messages.unshift(newMessage);
      conv.messageCount = (conv.messageCount || 0) + 1;
    }
  };

  if (state.currentConversation) {
    addMessageToList(state.currentConversation);
  }

  const settingId =
    newMessage.integrationSettingId?.uuid ||
    newMessage.integrationSettingId;
  if (settingId && state.conversations?.[settingId]) {
    addMessageToList(state.conversations[settingId]);
  }
};

const exchangeSlice = createSlice({
  name: "integrationSetting",
  initialState,
  reducers: {
    setSetting: (state, action) => {
      state.setting = action.payload;
    },
    clearSetting: (state) => {
      state.setting = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setLoading: (state, action) => {
      state.loading = !!action.payload;
    },
    updateSettingField: (state, action) => {
      if (state.setting) {
        state.setting = { ...state.setting, ...action.payload };
      }
    },
    updateSyncedMessageInConversation: (state, action) => {
      const updated = extractSyncedMessage(action.payload);
      applyUpdatedSyncedMessage(state, updated);
    },
    addSyncedMessageToConversation: (state, action) => {
      const created = extractSyncedMessage(action.payload);
      applyNewSyncedMessage(state, created);
    },
  },

  extraReducers: (builder) => {
    builder

      .addCase(upsertIntegrationSetting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(upsertIntegrationSetting.fulfilled, (state, action) => {
        state.loading = false;

        const updated = action.payload;

        const index = state.integrationSettings.findIndex(
          (item) => item.uuid === updated.uuid,
        );

        if (index !== -1) {
          const existing = state.integrationSettings[index];

          state.integrationSettings[index] = {
            ...existing,
            ...updated,
            integrationId:
              typeof existing.integrationId === "object"
                ? existing.integrationId
                : updated.integrationId,
          };
        } else {
          state.integrationSettings.push(updated);
        }

        state.setting = updated;
      })
      .addCase(upsertIntegrationSetting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchIntegrationSettingsByUserId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIntegrationSettingsByUserId.fulfilled, (state, action) => {
        state.loading = false;
        state.integrationSettings = action.payload;
      })
      .addCase(fetchIntegrationSettingsByUserId.rejected, (state, action) => {
        state.loading = false;
        state.integrationSettings = [];
        state.error = action.payload;
      })
      .addCase(deleteIntegrationSetting.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteIntegrationSetting.fulfilled, (state, action) => {
        state.loading = false;
        state.setting = null;
        state.error = null;

        const deletedUuid = action.meta.arg.uuid;

        state.integrationSettings = state.integrationSettings.filter(
          (item) => item.uuid !== deletedUuid,
        );
      })
      .addCase(deleteIntegrationSetting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchIntegrationSettingById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIntegrationSettingById.fulfilled, (state, action) => {
        state.loading = false;
        state.setting = action.payload;
      })
      .addCase(fetchIntegrationSettingById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(refreshIntegrationSetting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshIntegrationSetting.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(refreshIntegrationSetting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchIntegrationConfiguration.pending, (state) => {
        state.configLoading = true;
        state.error = null;
      })
      .addCase(fetchIntegrationConfiguration.fulfilled, (state, action) => {
        state.configLoading = false;
        state.configurations = action.payload.configurations;
      })
      .addCase(fetchIntegrationConfiguration.rejected, (state, action) => {
        state.configLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchIntegrationConversation.pending, (state) => {
        state.conversationsLoading = true;
      })
      .addCase(fetchIntegrationConversation.fulfilled, (state, action) => {
        state.conversationsLoading = false;
        state.conversationGroups = parseGroups(action.payload);
      })
      .addCase(fetchIntegrationConversation.rejected, (state, action) => {
        state.conversationsLoading = false;
        state.error = action.payload;
      })

      .addCase(fetchConversationBySettingId.pending, (state) => {
        state.currentConversationLoading = true;
        state.error = null;
      })
      .addCase(fetchConversationBySettingId.fulfilled, (state, action) => {
        state.currentConversationLoading = false;
        const conversation = parseConversation(action.payload);

        state.currentConversation = conversation;
        state.currentConversationId = action.meta.arg;
        state.conversations[action.meta.arg] = conversation;
      })
      .addCase(fetchConversationBySettingId.rejected, (state, action) => {
        state.currentConversationLoading = false;
        state.error = action.payload;
      })

      .addCase(syncAllConversations.pending, (state) => {
        state.syncing = true;
        state.conversationsLoading = true;
        state.error = null;
      })
      .addCase(syncAllConversations.fulfilled, (state, action) => {
        state.syncing = false;
        state.conversationsLoading = false;

        const { groups, conversations } = action.payload;
        state.conversationGroups = groups;
        state.conversations = { ...state.conversations, ...conversations };

        const openId = state.currentConversationId;
        if (openId && conversations[openId] !== undefined) {
          state.currentConversation = conversations[openId];
        }
      })
      .addCase(syncAllConversations.rejected, (state, action) => {
        state.syncing = false;
        state.conversationsLoading = false;
        if (action.payload) state.error = action.payload;
      })

      .addCase(sendIntegrationMessage.pending, (state) => {
        state.sendingMessage = true;
        state.error = null;
      })
      .addCase(sendIntegrationMessage.fulfilled, (state, action) => {
        state.sendingMessage = false;
        const created = extractSyncedMessage(action.payload);
        applyNewSyncedMessage(state, created);
      })
      .addCase(sendIntegrationMessage.rejected, (state, action) => {
        state.sendingMessage = false;
        state.error = action.payload;
      })
      .addCase(sendMessageReply.pending, (state) => {
        state.sendingMessage = true;
        state.error = null;
      })
      .addCase(sendMessageReply.fulfilled, (state, action) => {
        state.sendingMessage = false;
        const updated = extractSyncedMessage(action.payload);
        applyUpdatedSyncedMessage(state, updated);
      })
      .addCase(sendMessageReply.rejected, (state, action) => {
        state.sendingMessage = false;
        state.error = action.payload;
      });
  },
});

export const {
  setSetting,
  clearSetting,
  clearError,
  setLoading,
  updateSettingField,
  updateSyncedMessageInConversation,
  addSyncedMessageToConversation,
} = exchangeSlice.actions;

export default exchangeSlice.reducer;
